import BenchlingConfig from 'BenchlingConfig';
import LocalStorage from 'LocalStorage';
import EntityStore from 'stores/EntityStore';

export const DRIVE_SCOPE = 'https://www.googleapis.com/auth/drive.file';
const USERINFO_URL = 'https://www.googleapis.com/oauth2/v3/userinfo';

export function getGDriveOAuthClientId(): string {
  return BenchlingConfig.ELN_GDRIVE_OAUTH_CLIENT_ID;
}

/**
 * Derives the Google Cloud project number from the OAuth client ID.
 * Client IDs follow the format `<project-number>-<hash>.apps.googleusercontent.com`.
 * This is the same value as "App ID" in the Google Drive Picker API.
 *
 * TODO: Consider storing the project number as its own config value
 * (e.g. ELN_GDRIVE_PROJECT_NUMBER) rather than deriving it from the
 * client ID. The two values are independent identifiers that happen to
 * share a prefix today, but that relationship is not guaranteed in the future.
 */
export function getGDriveProjectNumber(): string {
  const match = getGDriveOAuthClientId().match(/^(\d+)/);
  return match?.[1] ?? '';
}

function getLastEmailKey(): string {
  const userId = EntityStore.currentUserId;
  const userSegment = userId != null ? `:${userId}` : '';
  return `benchling:gdrive:last_authorized_email${userSegment}`;
}
// --- Last authorized email (for login_hint) ---

export function getLastAuthorizedEmail(): string | null {
  return LocalStorage.getItem(getLastEmailKey()) ?? null;
}

export function setLastAuthorizedEmail(email: string): void {
  LocalStorage.setItem(getLastEmailKey(), email);
}

export function clearLastAuthorizedEmail(): void {
  LocalStorage.removeItem(getLastEmailKey());
}

// --- Email resolution from access token ---

async function fetchGoogleEmail(accessToken: string): Promise<string | null> {
  try {
    const response = await fetch(USERINFO_URL, {
      headers: {Authorization: `Bearer ${accessToken}`},
    });
    if (!response.ok) {
      return null;
    }
    const data = await response.json();
    return data.email ?? null;
  } catch (err) {
    // fetch throws TypeError on network failures (DNS, connectivity, CORS)
    if (err instanceof TypeError) {
      return null;
    }
    throw err;
  }
}

/**
 * Fetches the user's email from a Google OAuth access token and persists
 * it as the last authorized email (used as `login_hint` in subsequent
 * token requests). Clears the stored email if the fetch fails, so that
 * callers can re-prompt the user for account selection.
 */
export async function fetchAndStoreAuthorizedEmail(accessToken: string): Promise<void> {
  const email = await fetchGoogleEmail(accessToken);
  if (email != null) {
    setLastAuthorizedEmail(email);
  } else {
    clearLastAuthorizedEmail();
  }
}

// --- Google Drive auth errors ---

export class GDriveAuthError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'GDriveAuthError';
  }
}

export class GDriveConsentDeniedError extends GDriveAuthError {
  constructor() {
    super('The user denied access to Google Drive.');
    this.name = 'GDriveConsentDeniedError';
  }
}

export class GDriveMissingScopesError extends GDriveAuthError {
  readonly missingScopes: Array<string>;
  constructor(missingScopes: Array<string>) {
    super(`Missing required scopes: ${missingScopes.join(', ')}`);
    this.name = 'GDriveMissingScopesError';
    this.missingScopes = missingScopes;
  }
}

export class GDrivePopupClosedError extends GDriveAuthError {
  constructor() {
    super('The authorization popup was closed.');
    this.name = 'GDrivePopupClosedError';
  }
}

// --- GIS token client ---

interface GetTokenClientOptions {
  callback: (tokenResponse: google.accounts.oauth2.TokenResponse) => void;
  error_callback: (error: google.accounts.oauth2.ClientConfigError) => void;
  scope: string;
}

/**
 * Creates a fresh GIS token client per call so each request
 * has its own isolated callbacks.
 */
function getTokenClient(options: GetTokenClientOptions): google.accounts.oauth2.TokenClient {
  return window.google.accounts.oauth2.initTokenClient({
    client_id: getGDriveOAuthClientId(),
    scope: options.scope,
    callback: options.callback,
    error_callback: options.error_callback,
  });
}

// --- Token acquisition ---

export interface GetAccessTokenOptions {
  scopes: Array<string>;
  loginHint?: string;
  prompt?: string;
}

/**
 * Acquires a Google OAuth access token via the GIS token model.
 *
 * Creates a fresh token client, calls `requestAccessToken`, and verifies
 * that the returned token contains all requested scopes. Rejects if GIS
 * is not loaded, the user denies consent, or the granted scopes are
 * insufficient.
 */
export function getAccessToken(options: GetAccessTokenOptions): Promise<string> {
  if (!window.google?.accounts?.oauth2) {
    return Promise.reject(new GDriveAuthError('Google Identity Services not loaded.'));
  }

  const scopeStr = options.scopes.join(' ');

  return new Promise<string>((resolve, reject) => {
    const client = getTokenClient({
      scope: scopeStr,
      callback: (tokenResponse) => {
        if (tokenResponse.error) {
          if (tokenResponse.error === 'access_denied') {
            reject(new GDriveConsentDeniedError());
          } else {
            reject(new GDriveAuthError(`OAuth error: ${tokenResponse.error}`));
          }
          return;
        }
        const granted = new Set((tokenResponse.scope ?? '').split(' ').filter((s) => s));
        const missing = options.scopes.filter((s) => !granted.has(s));
        if (missing.length > 0) {
          reject(new GDriveMissingScopesError(missing));
          return;
        }
        resolve(tokenResponse.access_token);
      },
      error_callback: (error) => {
        if (error.type === 'popup_closed') {
          reject(new GDrivePopupClosedError());
        } else {
          reject(new GDriveAuthError(`GIS client error: ${error.type}`));
        }
      },
    });
    client.requestAccessToken({
      prompt: options.prompt ?? '',
      login_hint: options.loginHint ?? getLastAuthorizedEmail() ?? undefined,
    });
  });
}
